if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.productInfo = this.closest('product-info');

        this.setupLongrColorGallery();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');

            if (button) {
              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            }
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /* ---------------------------------------------
       * LØNGR FARBGALERIE
       * Alt-Texte:
       * Schwarz   = Schwarz
       * Off-White = off-white
       * --------------------------------------------- */

      setupLongrColorGallery() {
        window.setTimeout(() => {
          this.filterMediaByColor();
        }, 150);

        const container = this.productInfo || document;

        container.addEventListener('change', () => {
          window.setTimeout(() => {
            this.filterMediaByColor();
          }, 150);
        });

        /*
         * Zusätzlich CLICK überwachen.
         * Das macht den Farbwechsel auf Mobile robuster,
         * insbesondere bei Swatches / Radio-Buttons.
         */
        container.addEventListener('click', (event) => {
          const target = event.target.closest(
            'input[type="radio"], label, button'
          );

          if (!target) return;

          window.setTimeout(() => {
            this.filterMediaByColor();
          }, 200);
        });
      }

      getSelectedColor() {
        const container = this.productInfo || document;

        /*
         * Radio / Swatches
         */
        const checkedInputs = container.querySelectorAll(
          'input[type="radio"]:checked'
        );

        for (const input of checkedInputs) {
          const optionName = (
            input.dataset.optionName ||
            input.name ||
            ''
          ).toLowerCase();

          if (
            optionName.includes('farbe') ||
            optionName.includes('color')
          ) {
            return this.normalizeColor(input.value);
          }
        }

        /*
         * Select / Dropdown
         */
        const selects = container.querySelectorAll('select');

        for (const select of selects) {
          const optionName = (
            select.dataset.optionName ||
            select.name ||
            ''
          ).toLowerCase();

          if (
            optionName.includes('farbe') ||
            optionName.includes('color')
          ) {
            return this.normalizeColor(select.value);
          }
        }

        return null;
      }

      normalizeColor(value) {
        if (!value) return null;

        const color = value
          .trim()
          .toLowerCase()
          .replace(/_/g, '-');

        if (
          color === 'schwarz' ||
          color === 'black'
        ) {
          return 'schwarz';
        }

        if (
          color === 'off-white' ||
          color === 'offwhite' ||
          color === 'off white'
        ) {
          return 'off-white';
        }

        return color;
      }

      getMediaColor(item) {
        const image = item.querySelector('img');

        if (!image) return null;

        const alt = this.normalizeColor(
          image.getAttribute('alt') || ''
        );

        if (alt === 'schwarz') return 'schwarz';
        if (alt === 'off-white') return 'off-white';

        return null;
      }

      setMediaVisibility(item, visible) {
        if (visible) {
          item.hidden = false;
          item.removeAttribute('aria-hidden');
          item.style.removeProperty('display');
        } else {
          item.hidden = true;
          item.setAttribute('aria-hidden', 'true');

          /*
           * Wichtig für Dawn Mobile:
           * Slider darf das Bild überhaupt nicht mehr
           * als sichtbaren Slide berechnen.
           */
          item.style.setProperty(
            'display',
            'none',
            'important'
          );
        }
      }

      filterMediaByColor() {
        if (!this.elements.viewer) return;

        const selectedColor =
          this.getSelectedColor();

        if (!selectedColor) return;

        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(
            '[data-media-id]'
          )
        );

        let firstVisibleMedia = null;
        let visibleCount = 0;

        /*
         * Hauptgalerie filtern
         */
        mediaItems.forEach((item) => {
          const mediaColor =
            this.getMediaColor(item);

          const belongsToColorGroup =
            mediaColor === 'schwarz' ||
            mediaColor === 'off-white';

          const visible =
            !belongsToColorGroup ||
            mediaColor === selectedColor;

          this.setMediaVisibility(
            item,
            visible
          );

          if (visible) {
            visibleCount++;

            if (!firstVisibleMedia) {
              firstVisibleMedia = item;
            }
          }
        });

        /*
         * Thumbnail-Galerie filtern
         */
        if (this.elements.thumbnails) {
          const thumbnails =
            this.elements.thumbnails.querySelectorAll(
              '[data-target]'
            );

          thumbnails.forEach((thumbnail) => {
            const mediaColor =
              this.getMediaColor(thumbnail);

            const belongsToColorGroup =
              mediaColor === 'schwarz' ||
              mediaColor === 'off-white';

            const visible =
              !belongsToColorGroup ||
              mediaColor === selectedColor;

            this.setMediaVisibility(
              thumbnail,
              visible
            );
          });
        }

        /*
         * Prüfen, ob das aktive Medium jetzt
         * zur falschen Farbe gehört.
         */
        const activeMedia =
          this.elements.viewer.querySelector(
            '.is-active'
          );

        if (
          firstVisibleMedia &&
          (
            !activeMedia ||
            activeMedia.hidden ||
            activeMedia.style.display === 'none'
          )
        ) {
          this.setActiveMedia(
            firstVisibleMedia.dataset.mediaId,
            false
          );
        }

        /*
         * MOBILE besonders wichtig:
         * Slider zurücksetzen und an den Anfang springen.
         */
        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();

          this.elements.viewer.slider.scrollTo({
            left: 0,
            behavior: 'auto',
          });
        }

        if (
          this.elements.thumbnails &&
          this.elements.thumbnails.slider
        ) {
          this.elements.thumbnails.resetPages();

          this.elements.thumbnails.slider.scrollTo({
            left: 0,
            behavior: 'auto',
          });
        }

        /*
         * Mobile Bildzähler aktualisieren
         */
        const currentCounter =
          this.querySelector(
            '.slider-counter--current'
          );

        const totalCounter =
          this.querySelector(
            '.slider-counter--total'
          );

        if (
          currentCounter &&
          visibleCount > 0
        ) {
          currentCounter.textContent = '1';
        }

        if (
          totalCounter &&
          visibleCount > 0
        ) {
          totalCounter.textContent =
            visibleCount;
        }

        /*
         * Noch einmal nach kurzem Delay.
         * Dawn berechnet den Slider teilweise
         * zeitversetzt neu.
         */
        window.setTimeout(() => {
          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }

          if (
            this.elements.thumbnails &&
            this.elements.thumbnails.slider
          ) {
            this.elements.thumbnails.resetPages();
          }
        }, 100);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]:not([hidden])`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]:not([hidden])'
          );

        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove(
              'is-active'
            );
          });

        activeMedia.classList.add(
          'is-active'
        );

        if (prepend) {
          if (
            activeMedia.parentElement
              .firstChild !== activeMedia
          ) {
            activeMedia.parentElement.prepend(
              activeMedia
            );
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement
                .firstChild !== activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }

            if (
              this.elements.thumbnails.slider
            ) {
              this.elements.thumbnails.resetPages();
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            !this.mql.matches ||
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'auto',
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (
            activeMediaRect.top > -0.5
          ) {
            return;
          }

          const top =
            activeMediaRect.top +
            window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(
          activeMedia
        );

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(
          activeThumbnail
        );

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset
              .mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute(
              'aria-current'
            )
          );

        const button =
          thumbnail.querySelector('button');

        if (button) {
          button.setAttribute(
            'aria-current',
            true
          );
        }

        if (
          this.elements.thumbnails
            .isSlideVisible(
              thumbnail,
              10
            )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion
            .setAttribute(
              'aria-hidden',
              false
            );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings
              .imageAvailable.replace(
                '[index]',
                position
              );

          setTimeout(() => {
            this.elements.liveRegion
              .setAttribute(
                'aria-hidden',
                true
              );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) {
          return;
        }

        this.elements.viewer.slider
          .setAttribute(
            'role',
            'presentation'
          );

        this.elements.viewer.sliderItems
          .forEach((slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
          );
      }
    }
  );
}